import {
  ALL_CATEGORIES_ID,
  CACategoryModel,
  defaultFilter,
  Filter,
  FilterPublication,
  SortModel,
  sortTypes,
  Unknown,
} from '../models'
import type {AppStorage} from '../index'
import {assertKey} from '../utils/parse.utils'
import type {SavedFilterResponse} from './filterApi'

export const DAY_PARTS = ['once', 'morning', 'day', 'evening']

const applySort = (sort: SortModel | undefined, filter: any) => {
  if (!sort) return
  //filter.server_sort = 2 // distance
  filter.sortDirection = sort.direction
  filter.sortField = sort.key
}

interface SaveFilter {
  readonly id: number
  readonly slug?: string
  readonly value: number
}

interface SaveFilterRequest {
  readonly categoryID?: number
  readonly distanceMax?: number
  readonly distanceMin?: number
  readonly favorites?: []
  readonly fields: SaveFilter[]
  readonly id: null
  readonly latitude: number
  readonly longitude: number
  readonly notificationFrequencyInHours: number
  readonly notification?: {
    readonly partOfADay: string
    readonly isPushEnabled: boolean
    readonly isEmailEnabled: boolean
  }
  readonly onlyFavorites?: boolean
  readonly priceMax?: string | Unknown
  readonly priceMin?: string | Unknown
  readonly sortDirection: string
  readonly sortField: string
  readonly subscribe: boolean
  readonly title?: string
  readonly trackNew: boolean
  readonly trackUsed: boolean
  readonly withPhoto: boolean
}

export class FilterMapper {
  constructor(private storage: AppStorage) {}

  prepareAdsFilterQuery = (
    filter: Filter,
    options?: {
      readonly advHash?: string
      readonly cacheId?: string
      readonly withoutAppId?: boolean
      readonly withoutUserHash?: boolean
    },
  ): any => {
    const applyFilter: any = {}
    const uid = this.storage.userHash
    const radius = this.storage.searchRadius
    const withPhoto = filter.withPhoto ?? this.storage.withPhoto

    applyFilter.cacheId = options?.cacheId
    if (!options?.withoutAppId) {
      applyFilter.appId = this.storage.platform
    }
    if (!options?.withoutUserHash) {
      applyFilter.userHash = !!uid ? uid : undefined
    }

    applyFilter.onlyWithPhoto = withPhoto
    applyFilter.published = filter.published
    applyFilter.condition = filter.condition
    applyFilter.onlyFromSubscribed = filter.onlyFromSubscribed
    applySort(filter.sort, applyFilter)
    applyFilter.distanceMax = filter.distanceMax ?? radius
    if (filter.search.length !== 0) applyFilter.search = filter.search
    applyFilter.advHash = options?.advHash
    applyFilter.onlyDiscounted = filter.onlyDiscounted
    if (filter.location) {
      applyFilter.longitude = filter.location.longitude
      applyFilter.latitude = filter.location.latitude
    }
    if (filter.countryId) applyFilter.countryISO = filter.countryId
    if (filter.regionId) applyFilter.regionId = filter.regionId
    if (filter.cityId) applyFilter.cityId = filter.cityId
    applyFilter.notification = filter.notification

    if (filter.categoryId && filter.categoryId !== ALL_CATEGORIES_ID) {
      applyFilter.categoryId = filter.categoryId
    }
    if (!!filter.priceMin) applyFilter.priceMin = parseInt(filter.priceMin, 10)
    if (!!filter.priceMax) applyFilter.priceMax = parseInt(filter.priceMax, 10)
    applyFilter.fields = {}

    for (let [key, value] of filter.fieldValues) {
      assertKey(key)
      if (value.length === 2) {
        if (value.every((a: any) => !!a)) {
          applyFilter.fields[key] = value
        }
        continue
      }
      if (value.length === 0) continue
      if (value.every((a: any) => !!a)) applyFilter.fields[key] = value
    }

    return applyFilter
  }

  mapFilterToV2Api = (
    filter: Filter,
    extra: {
      readonly title?: string
      readonly latitude: number
      readonly longitude: number
    },
  ): SaveFilterRequest => {
    const fields: SaveFilter[] = []

    for (let [key, values] of filter.fieldValues) {
      assertKey(key)
      const filteredValues = values.filter((i) => !!i)
      if (filteredValues.length === 0) continue
      const value = filteredValues[filteredValues.length - 1] // as (number | undefined)
      if (!value) continue
      fields.push({id: key, value})
    }

    return {
      categoryID: filter.categoryId,
      distanceMax: filter.distanceMax,
      distanceMin: undefined, //filter.distanceMin,
      favorites: undefined, //'',
      fields,

      id: null,
      notificationFrequencyInHours: 2,
      notification: filter.notification
        ? {
            partOfADay: filter.notification?.partOfDay
              ? DAY_PARTS[filter.notification?.partOfDay]
              : 'day',
            isEmailEnabled: filter.notification.emailNotification,
            isPushEnabled: filter.notification.pushNotification,
          }
        : undefined,
      latitude: extra.latitude,
      longitude: extra.longitude,

      onlyFavorites: undefined, //filter,
      priceMax: filter.priceMax,
      priceMin: filter.priceMin,
      sortDirection: filter.sort.direction,
      sortField: filter.sort.key,
      subscribe: filter.onlyFromSubscribed,
      title: extra.title ?? filter.title,
      trackNew: true,
      trackUsed: false,
      withPhoto: filter.withPhoto ?? false,
    }
  }

  mapFilterRestV2ToModels = (restFilter: SavedFilterResponse): Filter => {
    const filter = defaultFilter(this.storage)

    filter.id = `${restFilter.id}`
    filter.title = restFilter.title

    filter.withPhoto = restFilter.isWithPhoto
    filter.condition = restFilter.condition
    filter.onlyFromSubscribed = restFilter.showOnlyFavorite

    filter.distanceMax = restFilter.distanceMax

    if (restFilter.countryId) filter.countryId = restFilter.countryId
    if (restFilter.regionId) filter.regionId = restFilter.regionId
    if (restFilter.cityId) filter.cityId = restFilter.cityId
    // @ts-ignore
    filter.categoryId = restFilter.categoryId
    if (!!restFilter.priceMin) filter.priceMin = restFilter.priceMin
    if (!!filter.priceMax) filter.priceMax = restFilter.priceMax

    if (restFilter.latitude && restFilter.longitude) {
      filter.location = {
        latitude: restFilter.latitude,
        longitude: restFilter.longitude,
      }
    }
    // @ts-ignore
    filter.notification = restFilter.notification

    console.warn('Fix onlyDiscounted field')

    if (restFilter.published) {
      switch (restFilter.published) {
        case FilterPublication.ALL_TIME:
          filter.published = FilterPublication.ALL_TIME
          break
        case FilterPublication.WITHIN_7_DAYS:
          filter.published = FilterPublication.WITHIN_7_DAYS
          break
        case FilterPublication.WITHIN_24_HOURS:
          filter.published = FilterPublication.WITHIN_24_HOURS
          break
        default:
          filter.published = FilterPublication.ALL_TIME
          console.error(`Unknown type ${restFilter.published}`)
      }
    }

    const fields = restFilter.fields ?? {}
    const keys = Object.keys(fields)
    for (let key of keys) {
      assertKey(key)
      // @ts-ignore
      filter.fieldValues.set(Number(key), fields[key])
    }

    const sort: SortModel | undefined =
      sortTypes.find(
        (s) =>
          s.key === restFilter.sortField &&
          s.direction === restFilter.sortDirection,
      ) ?? sortTypes[0]
    filter.sort = {...sort}

    return filter
  }

  mapRestFilterToApp = (restFilter: any): Filter => {
    const filter: Filter = defaultFilter(this.storage)

    filter.id = `${restFilter.id}`
    filter.title = restFilter.title

    filter.withPhoto = restFilter.withPhoto
    filter.condition = restFilter.condition
    filter.onlyFromSubscribed = restFilter.showOnlyFavorite

    filter.distanceMax = restFilter.distanceMax

    if (restFilter.countryId) filter.countryId = restFilter.countryId
    if (restFilter.regionId) filter.regionId = restFilter.regionId
    if (restFilter.cityId) filter.cityId = restFilter.cityId

    filter.categoryId = restFilter.categoryId
    if (!!restFilter.priceMin) filter.priceMin = restFilter.priceMin
    if (!!filter.priceMax) filter.priceMax = restFilter.priceMax

    if (restFilter.latitude && restFilter.longitude) {
      filter.location = {
        latitude: restFilter.latitude,
        longitude: restFilter.longitude,
      }
    }

    filter.notification = restFilter.notification

    console.warn('Fix onlyDiscounted field')

    if (restFilter.published) {
      switch (restFilter.published) {
        case FilterPublication.ALL_TIME:
          filter.published = FilterPublication.ALL_TIME
          break
        case FilterPublication.WITHIN_7_DAYS:
          filter.published = FilterPublication.WITHIN_7_DAYS
          break
        case FilterPublication.WITHIN_24_HOURS:
          filter.published = FilterPublication.WITHIN_24_HOURS
          break
        default:
          filter.published = FilterPublication.ALL_TIME
          console.error(`Unknown type ${restFilter.published}`)
      }
    }

    const fields = restFilter.fields ?? {}
    const keys = Object.keys(fields)
    for (let key of keys) {
      assertKey(key)
      filter.fieldValues.set(Number(key), fields[key])
    }

    const sort: SortModel | undefined =
      sortTypes.find(
        (s) =>
          s.key === restFilter.sortField &&
          s.direction === restFilter.sortDirection,
      ) ?? sortTypes[0]
    filter.sort = {...sort}

    return filter
  }

  findCategoryById = (
    id: number | undefined,
    categories: Array<CACategoryModel>,
  ): CACategoryModel | undefined => {
    if (!id) return undefined
    let items = [...categories]
    while (items.length > 0) {
      let model = items.shift()
      if (!model) return undefined
      if (model.id === id) {
        console.log('---- FOUND CATEGORY', model)
        return model
      }
      items.push(...model.items)
    }
    throw new Error(`CATEGORY is Not found ${id}`)
  }
}
